import React, { useEffect, useState, useRef } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { closeModal, SweetAlertShow } from "../master/Master";
import Swal from "sweetalert2";
import Master from "./Master";
import { Link } from "react-router-dom";
import usePrivateLink from "../config/usePrivateLink";
import {
  busAdd,
  busDelete,
  busList,
  busStatusUpdate,
  busUpdate,
} from "../../api/Transport";
import { formatToDate } from "../master/FormatToDateTime";
import { imgBaseUrl } from "../../api/api";
import DownloadExcel from "../master/DownloadExcel";
import { PrintPage } from "../master/Print";

export default function BusList() {
  const hasAccess = usePrivateLink();

  const [loading, setLoading] = useState(false); // initial/list fetch
  const [actionLoading, setActionLoading] = useState(false); // add/update/delete/status
  const [buses, setBuses] = useState([]);
  const [modalData, setModalData] = useState("");
  const [search, setSearch] = useState("");

  const [addImgPreview, setAddImgPreview] = useState(null);
  const [addRcPreview, setAddRcPreview] = useState(null);
  const [updateImgPreview, setUpdateImgPreview] = useState(null);
  const [updateRcPreview, setUpdateRcPreview] = useState(null);
  const addFormRef = useRef(null);

  const getBusList = async () => {
    setLoading(true);
    try {
      const response = await busList();
      if (response.data.status) {
        setBuses(response.data.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (id) => {
    setActionLoading(true);
    try {
      const response = await busStatusUpdate(id);
      if (response?.data?.status) {
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setActionLoading(false);
      getBusList();
    }
  };

  const newBus = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const formData = new FormData(e.target);
      const response = await busAdd(formData);
      if (response.data.status) {
        closeModal("AddBus");
        e.target.reset();
        setAddImgPreview(null);
        setAddRcPreview(null);
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setActionLoading(false);
      getBusList();
    }
  };

  const updateBus = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      const formData = new FormData(e.target);
      const response = await busUpdate(formData);
      if (response.data.status) {
        closeModal("UpdateBus");
        getBusList();
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setActionLoading(false);
    }
  };

  const deleteBus = async (id) => {
    setActionLoading(true);
    try {
      const response = await busDelete(id);
      if (response.data.status) {
        getBusList();
        SweetAlertShow("success", "Successfully", response?.data?.message);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
        error?.response?.message ??
        error?.message,
      );
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    getBusList();
  }, []);

  const insuranceInfo = (date) => {
    if (!date)
      return { label: "Not set", tone: "secondary", icon: "ri-shield-line" };
    const days = Math.ceil((new Date(date) - new Date()) / 86400000);
    if (days < 0)
      return {
        label: "Expired",
        tone: "danger",
        icon: "ri-error-warning-line",
      };
    if (days <= 30)
      return {
        label: formatToDate(date),
        tone: "warning",
        icon: "ri-alarm-warning-line",
      };
    return {
      label: formatToDate(date),
      tone: "success",
      icon: "ri-shield-check-line",
    };
  };

  const onPickFile = (e, setPreview) => {
    const file = e.target.files?.[0];
    if (!file) {
      setPreview(null);
      return;
    }
    setPreview(URL.createObjectURL(file));
  };

  const filteredBuses = buses.filter((b) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      b.bus_number?.toLowerCase().includes(q) ||
      b.driver_name?.toLowerCase().includes(q) ||
      b.driver_number?.includes(q)
    );
  });

  const activeCount = buses.filter((b) => b.status === "1").length;
  const inactiveCount = buses.length - activeCount;
  const expiringCount = buses.filter((b) => {
    if (!b.insurance_expiry_date) return false;
    const days = Math.ceil(
      (new Date(b.insurance_expiry_date) - new Date()) / 86400000,
    );
    return days <= 30;
  }).length;
  const totalSeats = buses.reduce(
    (sum, b) => sum + (Number(b.capacity) || 0),
    0,
  );

  const StatCard = ({ icon, label, value, tone }) => (
    <div className="col-6 col-md-3">
      <div
        className="d-flex align-items-center gap-2 p-3 rounded-3 h-100"
        style={{ background: "#f8f9fb" }}
      >
        <div
          className={`d-flex align-items-center justify-content-center bg-label-${tone} rounded-3`}
          style={{ width: 38, height: 38, fontSize: 17, flexShrink: 0 }}
        >
          <i className={icon} />
        </div>
        <div>
          <div className="fw-bold" style={{ fontSize: 18, lineHeight: 1 }}>
            {loading ? <span className="placeholder col-4" /> : value}
          </div>
          <small className="text-muted">{label}</small>
        </div>
      </div>
    </div>
  );

  const SkeletonRow = ({ i }) => (
    <tr key={i}>
      {Array.from({ length: 8 }).map((__, j) => (
        <td key={j}>
          <span className="placeholder-glow d-block">
            <span
              className="placeholder col-8"
              style={{ height: 14, borderRadius: 4 }}
            />
          </span>
        </td>
      ))}
    </tr>
  );

  const handleDownload = async (type = "excel") => {
    setLoading(true);
    try {
      const downloadData = [
        ...(filteredBuses?.map((item, index) => ({
          "Bus Number": item?.bus_number ?? "",
          "Driver Name": item?.driver_name ?? "",
          "Driver Number": item?.driver_number ?? "",
          Capacity: item?.capacity ?? "",
          "Insurance Expiry Date": item.insurance_expiry_date ?? "",
          Note: item.note ?? "",
        })) || []),
      ];
      if (downloadData.length) {
        if (type === "excel") {
          const now = new Date();
          const fileName = `Report_${now.getFullYear()}_${now.getMonth() + 1
            }_${now.getDate()}_${now.getHours()}_${now.getMinutes()}`;

          DownloadExcel(downloadData, [], fileName, []);
        } else {
          PrintPage(downloadData);
        }
      } else {
        SweetAlertShow("info", "Info", "No records found");
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row py-5">
                  <Master />
                  <div className="col-md-12">
                    {/* Header card */}
                    <div className="card p-3 mb-3 shadow-sm border-0">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-3">
                        <div className="d-flex align-items-center gap-3">
                          <div
                            style={{
                              width: 48,
                              height: 48,
                              borderRadius: "14px",
                              background:
                                "linear-gradient(135deg,#185fa5,#4f8cff)",
                              color: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 22,
                              boxShadow: "0 4px 12px rgba(24,95,165,0.25)",
                            }}
                          >
                            <i className="ri-bus-line" />
                          </div>
                          <div>
                            <h5 className="m-0 fw-bold">Bus List</h5>
                            <small className="text-muted">
                              View & manage all buses
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <div
                            className="input-group"
                            style={{ maxWidth: 240 }}
                          >
                            <span className="input-group-text bg-transparent border-end-0">
                              <i className="ri-search-line text-muted" />
                            </span>
                            <input
                              type="text"
                              className="form-control border-start-0"
                              placeholder="Search bus, driver, phone…"
                              value={search}
                              onChange={(e) => setSearch(e.target.value)}
                            />
                          </div>
                          {hasAccess("/bus/export") && (
                            <button
                              type="button"
                              className="btn btn-outline-primary rounded-3 me-1"
                              onClick={() => handleDownload("excel")}
                            >
                              <i className="ri-file-excel-2-line me-2"></i>{" "}
                              Download
                            </button>
                          )}
                          {hasAccess("/bus/print") && (
                            <button
                              type="button"
                              className="btn btn-outline-info rounded-3"
                              onClick={() => handleDownload("print")}
                            >
                              <i className="ri-printer-line me-2"></i> Print
                            </button>
                          )}
                          {hasAccess("/bus/add") && (
                            <Link
                              to=""
                              data-bs-toggle="modal"
                              data-bs-target="#AddBus"
                              title="Add New Bus"
                              className="btn btn-primary d-flex align-items-center gap-1 px-3"
                            >
                              <i className="ri-add-line" />
                              Add Bus
                            </Link>
                          )}
                        </div>
                      </div>

                      {/* Stat strip */}
                      <div className="row g-2">
                        <StatCard
                          icon="ri-bus-2-line"
                          label="Total buses"
                          value={buses.length}
                          tone="primary"
                        />
                        <StatCard
                          icon="ri-checkbox-circle-line"
                          label="Active"
                          value={activeCount}
                          tone="success"
                        />
                        <StatCard
                          icon="ri-pause-circle-line"
                          label="Inactive"
                          value={inactiveCount}
                          tone="secondary"
                        />
                        <StatCard
                          icon="ri-alarm-warning-line"
                          label="Insurance due ≤30d"
                          value={expiringCount}
                          tone={expiringCount ? "warning" : "secondary"}
                        />
                      </div>
                    </div>

                    {/* Table card */}
                    <div className="card border-0 shadow-sm">
                      <div className="card-body">
                        <div className="table-responsive">
                          <table className="table table-hover align-middle mb-0">
                            <thead>
                              <tr style={{ background: "#f8f9fb" }}>
                                <th className="ps-3">#</th>
                                <th>
                                  <i className="ri-bus-2-line me-1 text-muted" />
                                  Bus
                                </th>
                                <th>
                                  <i className="ri-user-3-line me-1 text-muted" />
                                  Driver
                                </th>
                                <th>
                                  <i className="ri-phone-line me-1 text-muted" />
                                  Contact
                                </th>
                                <th>
                                  <i className="ri-group-line me-1 text-muted" />
                                  Capacity
                                </th>
                                <th>
                                  <i className="ri-shield-check-line me-1 text-muted" />
                                  Insurance
                                </th>
                                <th>
                                  <i className="ri-toggle-line me-1 text-muted" />
                                  Status
                                </th>
                                <th className="text-end pe-3">
                                  <i className="ri-settings-3-line me-1 text-muted" />
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {loading ? (
                                Array.from({ length: 6 }).map((_, i) => (
                                  <SkeletonRow key={i} i={i} />
                                ))
                              ) : filteredBuses.length === 0 ? (
                                <tr>
                                  <td colSpan={8} className="text-center py-5">
                                    <div className="d-flex flex-column align-items-center text-muted">
                                      <i
                                        className="ri-bus-line"
                                        style={{ fontSize: 48, opacity: 0.25 }}
                                      />
                                      <div className="mt-2 fw-semibold">
                                        {search
                                          ? "No buses match your search"
                                          : "No buses found"}
                                      </div>
                                      <small>
                                        {search
                                          ? "Try a different keyword"
                                          : "Add a bus to get started"}
                                      </small>
                                    </div>
                                  </td>
                                </tr>
                              ) : (
                                filteredBuses.map((bus, index) => {
                                  const insurance = insuranceInfo(
                                    bus.insurance_expiry_date,
                                  );
                                  const isActive = bus.status === "1";
                                  return (
                                    <tr key={bus.id}>
                                      <td className="ps-3 text-muted">
                                        {index + 1}
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          {bus.bus_image ? (
                                            <img
                                              src={imgBaseUrl + bus.bus_image}
                                              alt={bus.bus_number}
                                              style={{
                                                width: 38,
                                                height: 38,
                                                borderRadius: "10px",
                                                objectFit: "cover",
                                                flexShrink: 0,
                                              }}
                                            />
                                          ) : (
                                            <div
                                              className="rounded-3 d-flex align-items-center justify-content-center bg-label-primary"
                                              style={{
                                                width: 38,
                                                height: 38,
                                                flexShrink: 0,
                                              }}
                                            >
                                              <i className="ri-bus-2-fill" />
                                            </div>
                                          )}
                                          <span className="fw-medium">
                                            {bus.bus_number}
                                          </span>
                                        </div>
                                      </td>
                                      <td>
                                        <div className="d-flex align-items-center gap-2">
                                          <i className="ri-account-circle-line text-muted" />
                                          {bus.driver_name}
                                        </div>
                                      </td>
                                      <td>
                                        {bus.driver_number ? (
                                          <a
                                            href={`tel:${bus.driver_number}`}
                                            className="text-decoration-none d-flex align-items-center gap-1"
                                          >
                                            <i className="ri-phone-fill text-success" />
                                            {bus.driver_number}
                                          </a>
                                        ) : (
                                          <span className="text-muted">—</span>
                                        )}
                                      </td>
                                      <td>
                                        <span className="badge bg-label-info">
                                          <i className="ri-group-line me-1" />
                                          {bus.capacity ?? "—"}
                                        </span>
                                      </td>
                                      <td>
                                        <span
                                          className={`badge bg-label-${insurance.tone}`}
                                        >
                                          <i
                                            className={`${insurance.icon} me-1`}
                                          />
                                          {insurance.label}
                                        </span>
                                      </td>
                                      <td>
                                        {hasAccess("/bus/status") && (

                                          <div className="d-flex align-items-center gap-2">
                                            <div className="form-check form-switch m-0">
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                role="switch"
                                                checked={isActive}
                                                disabled={actionLoading}
                                                onChange={() =>
                                                  handleStatusUpdate(bus?.id)
                                                }
                                              />
                                            </div>
                                            <small
                                              className={
                                                isActive
                                                  ? "text-success"
                                                  : "text-muted"
                                              }
                                            >
                                              {isActive ? "Active" : "Inactive"}
                                            </small>
                                          </div>
                                        )}

                                      </td>
                                      <td className="pe-3">
                                        <div className="d-flex justify-content-end gap-2">
                                          {hasAccess("/bus/update") && (
                                            <button
                                              className="btn btn-sm btn-icon btn-outline-primary"
                                              title="Edit"
                                              onClick={() => {
                                                setModalData(bus);
                                                setUpdateImgPreview(
                                                  bus.bus_image
                                                    ? imgBaseUrl + bus.bus_image
                                                    : null,
                                                );
                                                setUpdateRcPreview(
                                                  bus.bus_rc_photo
                                                    ? imgBaseUrl +
                                                    bus.bus_rc_photo
                                                    : null,
                                                );
                                              }}
                                              data-bs-toggle="modal"
                                              data-bs-target="#UpdateBus"
                                            >
                                              <i className="ri-pencil-line" />
                                            </button>
                                          )}
                                          {hasAccess("/bus/delete") && (
                                            <button
                                              className="btn btn-sm btn-icon btn-outline-danger"
                                              title="Delete"
                                              onClick={() =>
                                                Swal.fire({
                                                  title: "Are you sure?",
                                                  text: "This action will permanently delete the bus!",
                                                  icon: "warning",
                                                  showCancelButton: true,
                                                  confirmButtonColor: "#d33",
                                                  cancelButtonColor: "#6c757d",
                                                  confirmButtonText:
                                                    "Yes, delete it!",
                                                  cancelButtonText: "Cancel",
                                                }).then((result) => {
                                                  if (result.isConfirmed)
                                                    deleteBus(bus.id);
                                                })
                                              }
                                            >
                                              <i className="ri-delete-bin-line" />
                                            </button>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })
                              )}
                            </tbody>
                          </table>
                        </div>
                        {!loading && filteredBuses.length > 0 && (
                          <div className="d-flex justify-content-between align-items-center pt-3 mt-2 border-top">
                            <small className="text-muted">
                              Showing {filteredBuses.length} of {buses.length}{" "}
                              buses
                            </small>
                            <small className="text-muted">
                              <i className="ri-group-line me-1" />
                              {totalSeats} total seats
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Bus Modal */}
      <div className="modal fade" id="AddBus" tabIndex={-1} aria-hidden="true">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={newBus} ref={addFormRef}>
              <div className="modal-header bg-label-primary p-4">
                <h1 className="modal-title fs-5 d-flex align-items-center gap-2">
                  <i className="ri-bus-2-line" /> Add New Bus
                </h1>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter bus number RJ01XX1234"
                        required
                        name="bus_number"
                        onChange={(e) =>
                          (e.target.value = e.target.value.toUpperCase())
                        }
                      />
                      <label>Bus Number</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter driver name"
                        name="driver_name"
                        onChange={(e) =>
                          (e.target.value = e.target.value.toUpperCase())
                        }
                        required
                      />
                      <label>Driver Name</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter driver number"
                        name="driver_number"
                        maxLength={10}
                        minLength={10}
                        pattern="\d{10}"
                        title="Please enter exactly 10 digits"
                        required
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9]/g,
                          "",
                        ))
                        }
                      />
                      <label>Driver Number</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter capacity"
                        name="capacity"
                        required
                      />
                      <label>Capacity</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="date"
                        className="form-control"
                        name="insurance_expiry_date"
                      />
                      <label>Insurance Expiry Date</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <textarea
                        className="form-control"
                        placeholder="Note"
                        name="note"
                      />
                      <label>Note</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-flex align-items-center gap-1">
                        <i className="ri-image-line" /> Bus Image
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="bus_image"
                        accept="image/*"
                        onChange={(e) => onPickFile(e, setAddImgPreview)}
                      />
                      {addImgPreview && (
                        <img
                          src={addImgPreview}
                          alt="preview"
                          className="mt-2 rounded"
                          style={{ width: 70, height: 70, objectFit: "cover" }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-flex align-items-center gap-1">
                        <i className="ri-file-shield-2-line" /> RC Photo
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="bus_rc_photo"
                        accept="image/*"
                        onChange={(e) => onPickFile(e, setAddRcPreview)}
                      />
                      {addRcPreview && (
                        <img
                          src={addRcPreview}
                          alt="preview"
                          className="mt-2 rounded"
                          style={{ width: 70, height: 70, objectFit: "cover" }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Update Bus Modal */}
      <div
        className="modal fade"
        id="UpdateBus"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <form onSubmit={updateBus}>
              <div className="modal-header bg-label-primary p-4">
                <h1 className="modal-title fs-5 d-flex align-items-center gap-2">
                  <i className="ri-pencil-line" /> Update Bus
                </h1>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6">
                    <input
                      type="hidden"
                      name="id"
                      value={modalData?.id || ""}
                    />
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        required
                        name="bus_number"
                        onChange={(e) => {
                          setModalData({
                            ...modalData,
                            bus_number: e.target.value.toUpperCase(),
                          });
                        }}
                        value={modalData?.bus_number}
                      />
                      <label>Bus Number</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        name="driver_name"
                        required
                        onChange={(e) => {
                          setModalData({
                            ...modalData,
                            driver_name: e.target.value.toUpperCase(),
                          });
                        }}
                        value={modalData?.driver_name}
                      />
                      <label>Driver Name</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        name="driver_number"
                        maxLength={10}
                        minLength={10}
                        pattern="\d{10}"
                        title="Please enter exactly 10 digits"
                        required
                        onInput={(e) =>
                        (e.target.value = e.target.value.replace(
                          /[^0-9]/g,
                          "",
                        ))
                        }
                        onChange={(e) => {
                          setModalData({
                            ...modalData,
                            driver_number: e.target.value.toUpperCase(),
                          });
                        }}
                        value={modalData?.driver_number}
                      />
                      <label>Driver Number</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="number"
                        className="form-control"
                        name="capacity"
                        required
                        onChange={(e) => {
                          setModalData({
                            ...modalData,
                            capacity: e.target.value.toUpperCase(),
                          });
                        }}
                        value={modalData?.capacity}
                      />
                      <label>Capacity</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <input
                        type="date"
                        className="form-control"
                        name="insurance_expiry_date"
                        onChange={(e) => {
                          setModalData({
                            ...modalData,
                            insurance_expiry_date: e.target.value,
                          });
                        }}
                        value={modalData?.insurance_expiry_date}
                      />
                      <label>Insurance Expiry Date</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3 form-floating form-floating-outline">
                      <textarea
                        className="form-control"
                        name="note"
                        onChange={(e) => {
                          setModalData({
                            ...modalData,
                            note: e.target.value,
                          });
                        }}
                        value={modalData?.note}
                      />
                      <label>Note</label>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-flex align-items-center gap-1">
                        <i className="ri-image-line" /> Bus Image
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="bus_image"
                        accept="image/*"
                        onChange={(e) => onPickFile(e, setUpdateImgPreview)}
                      />
                      {updateImgPreview && (
                        <img
                          src={updateImgPreview}
                          alt="preview"
                          className="mt-2 rounded"
                          style={{ width: 70, height: 70, objectFit: "cover" }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label d-flex align-items-center gap-1">
                        <i className="ri-file-shield-2-line" /> RC Photo
                      </label>
                      <input
                        type="file"
                        className="form-control"
                        name="bus_rc_photo"
                        accept="image/*"
                        onChange={(e) => onPickFile(e, setUpdateRcPreview)}
                      />
                      {updateRcPreview && (
                        <img
                          src={updateRcPreview}
                          alt="preview"
                          className="mt-2 rounded"
                          style={{ width: 70, height: 70, objectFit: "cover" }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-5"
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="ri-save-line me-1" />
                      Submit
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
